import React from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState } from '../../../../stores/types/types';
import { ContentTemplate, Field } from '../../../types/stylepacks';
import SelectInput, { SelectOption } from '../../../general-components/select-dropdown';
import { getAssetURL } from '../../../style-packs/style-pack';
import colors from '../../../../utils/colors';
import { ColorMapping } from '../../../../stores/types/themes';

const calcBorderColor = (compare1: any, compare2: any) => compare1 === compare2 ? '5px solid rgb(53, 252, 255)' : '5px solid rgba(0,0,0,0)';
const createTemplateKey = (t: ContentTemplate) => `${t.type}:${t.contentType}`;

export default function DebugCreateThemeMap() {
	const {defaultThemes} = useSelector((s: GreenroomState) => s.ThemesReducer)

	const contentTemplates = useSelector((s: GreenroomState) => s.GeneratedContentReducer.contentTemplates)

	const renderedTemplates = React.useMemo(() => {
		const mapping = contentTemplates.reduce((curr, acc) => {
			const key = createTemplateKey(acc);
			const existing = curr.get(key);
			if (!existing) {
				curr.set(key, {
					label: key,
					value: acc,
					icon: <img style={{maxWidth: '100px'}} src={getAssetURL(acc?.thumbnailUrl ?? acc.data?.thumbnail?.url ?? '')}/>
				});
			}
			return curr;
		}, new Map());
		return Array.from(mapping.values()).sort((a, b) => a.value.name.toLowerCase().localeCompare(b.value.name.toLowerCase()))
	}, [contentTemplates])


	const [selectedTemplate, setSelectedTemplate] = React.useState(renderedTemplates[0].value)
	
	const renderedThemes = React.useMemo(() => {
		const mapping = defaultThemes.filter((t) => t.stylepack === selectedTemplate.name).map((t) => {
			return {
				label: t.name,
				value: t,
				icon: <div style={{display: 'flex', marginRight: '4px'}}>
					{t.colors.map((c, i) => i>2 ? null :<div style={{
						height: '20px',
						width: '20px',
						marginBottom: '4px',
						backgroundColor: c
					}}/>)}
				</div>
			}
		});
		return mapping;
	}, [defaultThemes, selectedTemplate])

	const [selectedTheme, setSelectedTheme] = React.useState(renderedThemes[0].value)
	const [selectedField, setSelectedField] = React.useState('');
	const [opacityMap, setOpacityMap] = React.useState<Record<string, number>>({});
	const [selectedColorIndex, setSelectedColorIndex] = React.useState(-1);
	const [showFriendlyLabel, setShowFriendlyLabel] = React.useState(true)

	const [workingThemeMap, setWorkingThemeMap] = React.useState<ColorMapping>({
		templateKey: createTemplateKey(selectedTemplate),
		fieldToColorIndex: {},
		fieldToOpacity: {}
	})
	
	const chooseTemplate = (templateOp: SelectOption) => {
		setOpacityMap({})
		setSelectedTemplate(templateOp.value)
		setWorkingThemeMap((m) => ({
			...m,
			templateKey: createTemplateKey(templateOp.value),
			fieldToColorIndex: {}
		}))
	}
	const chooseTheme = (themeOp: SelectOption) => {
		const theme = defaultThemes.find((t) => t.name === themeOp.label)
		if (theme) {
			setOpacityMap({})
			setSelectedTheme(theme)
			setWorkingThemeMap((m) => ({
				...m,
				fieldToColorIndex: {}
			}))
		}
	}

	const chooseField = (f: string) => {
		if (selectedColorIndex !== -1) {
			setWorkingThemeMap((m) => ({
				...m,
				fieldToColorIndex: {
					...m.fieldToColorIndex,
					[f]: selectedColorIndex
				},
			}))
			setSelectedColorIndex(-1);
		} else {
			setSelectedField(f);
		}
	}

	const chooseColor = (index: number) => {
		if (selectedField) {
			setWorkingThemeMap((m) => ({
				...m,
				fieldToColorIndex: {
					...m.fieldToColorIndex,
					[selectedField]: index
				},
			}))
			setSelectedField('')
		} else {
			setSelectedColorIndex(index);
		}
	}

	const colorFields = React.useMemo((): Field[] => {
		if (!selectedTemplate?.data?.variables) return []
		const fields = selectedTemplate.data?.variables.filter((f: Field) => f.type === 'color')
		return fields;
	}, [selectedTemplate?.data?.variables])

	const copyWorkingMap = () => {
		const jsonToString = `"${workingThemeMap.templateKey}": ${JSON.stringify(workingThemeMap)},`
		navigator.clipboard.writeText(jsonToString);
	}

	return (<div>
		<div className='flex'>
			<div style={{flex: 13, marginRight: '8px'}}>
				<SelectInput 
					options={renderedTemplates}
					selected={createTemplateKey(selectedTemplate)}
					ignoreSpacing
					hideEmptyOption
					noMaxHeight
					showOptionIcon
					onChange={() => {}}
					onChangeFull={(op) => chooseTemplate(op)}
					dropdownClass='be-wider'
					keyType='label'
				/>
			</div>
			<div style={{flex: 9}}>
				<SelectInput 
					options={renderedThemes}
					selected={selectedTheme.name}
					small
					ignoreSpacing
					hideEmptyOption
					noMaxHeight
					showOptionIcon
					onChange={() => {}}
					onChangeFull={(op) => chooseTheme(op)}
					keyType='label'
				/>
			</div>
		</div>

		<div className='flex'>
			<div>
				<div>Show friendly labels <input type='checkbox' defaultChecked onChange={() => setShowFriendlyLabel((o) => !o)}></input></div>
				
				<div>
					{Object.entries(workingThemeMap.fieldToColorIndex).map(([key, value], index) => {
						return <div>{index + 1}. {key} {value}</div>
					})}
				</div>
				{Object.values(workingThemeMap.fieldToColorIndex).length < colorFields.length 
					? <div style={{color: 'red'}}>Missing fields: {colorFields.length - Object.values(workingThemeMap.fieldToColorIndex).length}</div>
					: <button style={{padding: '4px 8px'}} onClick={copyWorkingMap}>Copy Map</button>}
				<textarea 
					placeholder={`Paste into\nsrc/utils/stylepacks\n/theme-maps.ts`}
					style={{
						background: 'black',
						color: 'white',
						fontSize: '12px',
						lineHeight: '16px',
						padding: '8px',
						margin: '4px 0',
						width: '180px'
					}}
				/>
			</div>
			<div style={{margin: '0 12px'}}>
				{colorFields.map((f) => {
					const colorIndex = workingThemeMap.fieldToColorIndex[f.userData];
					const backgroundColor: string = (colorIndex || colorIndex === 0)
						? selectedTheme.colors[colorIndex]
						: f.default as string;
					return <>
						<div 
							className='template-chip'
							onClick={() => chooseField(f.userData)}
							style={{
								border: calcBorderColor(selectedField, f.userData),
								backgroundColor: colors.blGrayscale700,
								borderRadius: '12px',
								width: 'fit-content',
								padding: '2px 4px 4px 4px',
								gap: '8px',
								marginBottom: '8px',
								textWrap: 'nowrap',
								marginLeft: 'auto'
							}}
						>
							<div
								className='bl-font-s3'
								style={{color: (colorIndex || colorIndex === 0) ? colors.paleGreen : colors.nearWhite}}
							>
								<div>{showFriendlyLabel ? f.name : f.userData}</div>
							</div>
							<div className='flex'>
								<div className='template-chip-color' style={{
									backgroundColor,
									width: '100%',
									height: '20px',
									marginTop: '2px',
									borderRadius: '99px',
									display: 'flex',
									justifyContent: 'center'
								}}/>
							</div>
						</div>
					</>
				})}
			</div>
			<div >
				{selectedTheme.colors.map((c, i) => <div 
					style={{
						height: '48px',
						width: '95px',
						marginBottom: '4px',
						borderRadius:'4px',
						backgroundColor: c,
						boxSizing: 'content-box',
						border: calcBorderColor(selectedColorIndex, i),
						display: 'flex',
						justifyContent: 'space-between'
					}} 
					onClick={() => chooseColor(i)}
					key={`${c}-${i}-${opacityMap[i]}`}
				>
					<div>
						<div className='bl-font-s3'style={{color: 'black'}}>{c}</div>
						<div className='bl-font-s3'style={{color: 'white'}}>{c}</div>
					</div>
					<div style={{textAlign: 'right'}}>
						<span className='bl-font-s3'style={{color: 'black'}}>{i}/</span>
						<span className='bl-font-s3'style={{color: 'white'}}>/{i}</span>
					</div>
				</div>)}
			</div>
		</div>
	</div>);
}